import { QuestionCircleOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import {
  anchorIndexes,
  anchorNameForRole,
  assignAnchors,
  MAX_DATE_PLACEMENTS_PER_SIGNATURE,
  MAX_INPUT_PLACEMENTS_PER_SIGNATURE,
  roleExpectsSignature,
} from '@bmo/form-core';
import { Button, Empty, Segmented, Select, Tag, Tooltip } from 'antd';
import type { PageViewport } from 'pdfjs-dist';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  type FileEntryCondition,
  type OneSpanConfig,
  type SignatoryRole,
  type SignatureDocument,
  type SignatureField,
  type SignaturePlacement,
  type WorkflowStep,
  type WorkflowTemplate,
  workflowsApi,
} from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { PageHeader } from '@/components/PageHeader';
import { StatusTag } from '@/components/StatusTag';
import { handleApiError } from '@/lib/handleApiError';

import { advisorRole, withAdvisorRole } from './advisorRole';
import { ConditionsEditorModal } from './conditions/ConditionsEditorModal';
import { hasEligibleSourceFields } from './conditions/sourceFields';
import {
  DEFAULT_DATE_FONT_SIZE,
  DEFAULT_DATE_FORMAT,
  useDateTextMeasure,
} from './dateFormatMetrics';
import { collectPdfEntryOptions } from './pdfFieldMapping';
import { DEFAULT_ZOOM_INDEX, ZOOM_STEPS } from './pdfPageRender';
import {
  INPUT_BOX,
  boxSizeFor,
  defaultDatePlacement,
  defaultInputPlacement,
  duplicateDatePlacement,
  duplicateInputPlacement,
  placementAtCssPoint,
} from './pdfPlacementGeometry';
import { PdfSignaturePlacementViewer, type PlacedBox } from './PdfSignaturePlacementViewer';
import { completingBoxIsInitials, indexOfFieldToComplete } from './signatureFieldCompletion';
import { useWorkflowEditorSession } from './WorkflowEditorSession';

const I18N = 'signaturePlacement';

/**
 * Per-signatory box colour. Fixed palette indexed by position rather than a hash
 * of the slug, so the colours stay stable and distinguishable — a hash gives no
 * guarantee that two signatories on the same form do not land on near-identical
 * hues.
 */
const SIGNER_COLORS = ['#0179C1', '#059669', '#b45309', '#7c3aed', '#be123c', '#0f766e'] as const;

/**
 * Markers that distinguish a signature's companion boxes from the signature
 * itself.
 *
 * A signature can carry several of each, so the key carries the companion's
 * position in that language's list too — `[Anchor]::date::1`. Anchors are unique
 * within a document by construction, so anchor + kind + index identifies any box
 * on the page.
 */
const COMPANION_KEY_SUFFIX = { date: '::date::', input: '::input::' } as const;

/** The drag/delete key for one of a signature's companion boxes. */
function companionKey(anchor: string, kind: 'date' | 'input', index: number): string {
  return `${anchor}${COMPANION_KEY_SUFFIX[kind]}${index}`;
}

/**
 * Split a box key back into the field it belongs to, which kind of box it is, and
 * — for a companion — which of that field's boxes of that kind. `index` is null
 * for the signature itself.
 */
function parseBoxKey(key: string): {
  anchor: string;
  kind: 'signature' | 'date' | 'input';
  index: number | null;
} {
  for (const kind of ['date', 'input'] as const) {
    const suffix = COMPANION_KEY_SUFFIX[kind];
    const at = key.lastIndexOf(suffix);
    if (at !== -1) {
      return { anchor: key.slice(0, at), kind, index: Number(key.slice(at + suffix.length)) };
    }
  }
  return { anchor: key, kind: 'signature', index: null };
}

/**
 * The lowest `sig_field_N` / `field_N` number not already used in this document.
 *
 * Must NOT be `fields.length + 1`: once a field is deleted, the surviving names
 * are no longer a dense `1..length` run (e.g. deleting #2 from
 * `sig_field_1..4` leaves `1, 3, 4`), so `length + 1` mints a number — `4` — that
 * a live field already holds. `WorkflowStep.SetOneSpanConfig` refuses the whole
 * save with a duplicate-name `DOMAIN_INVARIANT` when that happens. Scanning the
 * existing suffixes and taking the lowest gap keeps every name unique no matter
 * what was deleted.
 */
function nextFieldNumber(fields: readonly SignatureField[]): number {
  const used = new Set(
    fields
      .map((f) => /^sig_field_(\d+)$/.exec(f.name)?.[1])
      .filter((s): s is string => s !== undefined)
      .map(Number),
  );
  let n = 1;
  while (used.has(n)) n++;
  return n;
}

/**
 * Drag-and-drop authoring for where each signatory signs.
 *
 * The originator drags a box onto a rendered PDF page; the anchor string is
 * generated from the signatory's display name and stamped into the generated
 * document as invisible white text at PDF-generation time. Each box collects
 * either a full signature or that signatory's initials — the toggle on the box
 * switches it, and the only thing the choice changes downstream is the `subtype`
 * BMO is told to tag with (`CAPTURE` vs `INITIALS`).
 *
 * Before drag-and-drop placement existed, the originator was expected to type
 * that white text into the PDF by hand and re-type the identical literal into
 * the e-signature drawer — two hand-authored halves that could disagree with
 * nothing able to detect it.
 *
 * Reached from the e-signature drawer's "Manage signature locations" button,
 * which saves the roster first: this page reads its signatory list from the
 * persisted config.
 *
 * EN and FR are authored INDEPENDENTLY. The two files are separate uploads with
 * different layouts, and an application generates exactly one of them — so a box
 * placed in one language and not the other is a signature that will never be
 * signed for applications in the other. The toolbar surfaces that gap and
 * `EnsureSubmittable` refuses it at submit.
 */
export function SignaturePlacementPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isFr = i18n.language.startsWith('fr');

  const goToDetails = useCallback(
    () => navigate(id ? `/originator/workflows/${id}` : '/originator/dashboard'),
    [navigate, id],
  );
  const { workflow, loading, lockBlock, patchWorkflow } = useWorkflowEditorSession();

  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [selectedPdfId, setSelectedPdfId] = useState<string | null>(null);
  const [lang, setLang] = useState<'en' | 'fr'>(isFr ? 'fr' : 'en');
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  const [saving, setSaving] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  /**
   * The signature whose show-conditions editor is open, by anchor. One modal at
   * the page root driven by a target, rather than one per box — the same pattern
   * `FormCanvas` uses for section conditions.
   */
  const [conditionAnchor, setConditionAnchor] = useState<string | null>(null);
  const zoom = ZOOM_STEPS[zoomIndex] ?? 1;
  const measureDate = useDateTextMeasure();

  const step = useMemo(
    () => (workflow ? findESignStep(workflow, searchParams.get('step')) : null),
    [workflow, searchParams],
  );
  const config = step?.oneSpanConfig ?? null;
  const pdfEntries = useMemo(() => (workflow ? collectPdfEntryOptions(workflow) : []), [workflow]);

  // Advisor pinned first, matching the drawer's card order, and always present:
  // the role is only persisted once a box references it (see `advisorRole.ts`),
  // and this page is the only place a box can be made — so offering the advisor
  // only when the role already exists would make them unplaceable forever.
  // A signatory the originator marked as expecting no signatures is left out: they are a party
  // on the application, not a signer, and offering them here would author a placement the
  // domain refuses on save.
  const roles = useMemo(() => {
    const all = config?.roles ?? [];
    return [
      advisorRole(all),
      ...all.filter((r) => r.type !== 'advisor' && roleExpectsSignature(r)),
    ];
  }, [config]);

  const colorBySlug = useMemo(
    () => new Map(roles.map((r, i) => [r.slug, SIGNER_COLORS[i % SIGNER_COLORS.length]!])),
    [roles],
  );

  useEffect(() => {
    const first = pdfEntries[0];
    if (selectedPdfId === null && first) setSelectedPdfId(first.id);
  }, [pdfEntries, selectedPdfId]);

  useEffect(() => {
    const first = roles[0];
    if (activeSlug === null && first) setActiveSlug(first.slug);
  }, [roles, activeSlug]);

  const selectedEntry = pdfEntries.find((e) => e.id === selectedPdfId) ?? null;
  const fileKey = selectedEntry
    ? lang === 'fr'
      ? selectedEntry.frFileKey
      : selectedEntry.enFileKey
    : null;

  const editable =
    lockBlock === null &&
    workflow !== null &&
    (workflow.status === 'Draft' || workflow.status === 'Rejected');

  const fields = useMemo(
    () => config?.documents.find((d) => d.pdfEntryId === selectedPdfId)?.fields ?? [],
    [config, selectedPdfId],
  );

  const labelBySlug = useMemo(
    () =>
      new Map(
        roles.map((r) => [
          r.slug,
          r.type === 'advisor'
            ? t(`${I18N}.advisor`)
            : ((isFr ? r.labelFr : r.labelEn) ?? anchorNameForRole(r)),
        ]),
      ),
    [roles, isFr, t],
  );

  /** Boxes for the document + language currently on screen. */
  const boxes = useMemo<PlacedBox[]>(() => {
    const out: PlacedBox[] = [];
    // One signatory can sign a document several times, and every one of those
    // boxes says the same name — so the badge carries the signature's ordinal
    // too. It is the anchor's own counter (`[SignatorySig1]` → "· 2"), taken
    // over every field in the document rather than the ones visible in this
    // language, so a signature is box 2 in both PDFs.
    const indexes = anchorIndexes(fields);
    fields.forEach((field, i) => {
      const signature = placementOf(field, lang);
      if (!signature) return;
      const color = colorBySlug.get(field.role) ?? SIGNER_COLORS[0];
      const signerLabel = labelBySlug.get(field.role) ?? field.role;
      const signerIndex = indexes[i]! + 1;
      const dates = datePlacementsOf(field, lang);
      const inputs = inputPlacementsOf(field, lang);
      out.push({
        key: field.textAnchor,
        anchor: field.textAnchor,
        kind: 'signature',
        // Sized from the initials flag rather than read straight from storage, the
        // same way a date box is measured from its tokens: the toggle writes the
        // new width to both languages at once, so this keeps the box on screen
        // honest even for the language that is not being edited.
        placement: { ...signature, ...boxSizeFor(field.isInitials ?? false) },
        signerLabel,
        signerIndex,
        color,
        active: field.role === activeSlug,
        hasDate: dates.length > 0,
        hasConditions: (field.conditions?.length ?? 0) > 0,
        isInitials: field.isInitials ?? false,
        atInputCap: inputs.length >= MAX_INPUT_PLACEMENTS_PER_SIGNATURE,
      });
      // A date box IS its placeholder text, so its footprint is measured from
      // the format tokens rather than read from storage. Deriving it here
      // rather than trusting the stored width/height is what lets a box
      // authored before these settings existed — a flat 150×40 — render
      // correctly on first load; the stored dimensions converge on the first
      // drag or settings save. The stored pair is the fallback for the frame
      // or two before the font metrics resolve.
      const { format, fontSize } = dateSettingsOf(field);
      const size = measureDate?.(format, fontSize);
      dates.forEach((date, dateIndex) => {
        out.push({
          key: companionKey(field.textAnchor, 'date', dateIndex),
          anchor: field.textAnchor,
          kind: 'date',
          placement: size ? { ...date, ...size } : date,
          signerLabel,
          signerIndex,
          color,
          active: field.role === activeSlug,
          hasDate: true,
          // A date follows its signature, so it carries no conditions of its own
          // and gets no gear — see `PdfSignaturePlacementViewer`. Same for the
          // initials toggle: a signing date reads the same either way.
          hasConditions: false,
          isInitials: false,
          dateText: format,
          dateFontSize: fontSize,
          dateIndex,
          dateCount: dates.length,
        });
      });
      // The input boxes, after the dates — the same order the payload emits them
      // in, so the page reads in the order the contract does.
      inputs.forEach((input, inputIndex) => {
        out.push({
          key: companionKey(field.textAnchor, 'input', inputIndex),
          anchor: field.textAnchor,
          kind: 'input',
          // Sized from the constant rather than from storage, the way a signature
          // is sized from `boxSizeFor`: there is no resize handle anywhere in this
          // UI, so a stored width is only ever a copy of `INPUT_BOX` taken at the
          // moment the box was added — never something the originator authored.
          // Deriving it here is what lets a box added before the width changed
          // render at the current footprint on first load; the stored pair
          // converges on the next drag or save. Position still comes from storage.
          placement: { ...input, ...INPUT_BOX },
          signerLabel,
          signerIndex,
          color,
          active: field.role === activeSlug,
          // An input follows its signature, so it carries no conditions, no
          // initials variant and no date of its own — see the date boxes above.
          hasDate: false,
          hasConditions: false,
          isInitials: false,
          inputIndex,
          inputCount: inputs.length,
        });
      });
    });
    return out;
  }, [fields, lang, colorBySlug, labelBySlug, activeSlug, measureDate]);

  /**
   * Persist a whole config. Every mutation here is a full PUT plus a refetch,
   * matching the field-mapping page's no-dirty-state precedent: the config is
   * small, and a drag fires this once on drag END rather than per frame.
   */
  const persist = useCallback(
    async (next: OneSpanConfig) => {
      if (!id || !step || !editable) return;
      setSaving(true);
      try {
        // The advisor role follows their boxes: materialized here on the first
        // placement, dropped again when the last one goes. Reconciled BEFORE the
        // anchors, because `assignAnchors` names an anchor from its role and a
        // field whose role is missing from the list keeps its placeholder.
        const roles = withAdvisorRole(next.roles, next.documents);
        // Anchors are derived from the current display names on every write, so
        // a rename that happened in the drawer cannot leave a stale one here.
        await workflowsApi.setOneSpanConfig(id, step.id, {
          ...next,
          roles,
          documents: assignAnchors(roles, next.documents),
        });
        patchWorkflow(await workflowsApi.get(id));
      } catch (err) {
        handleApiError(err, t, t(`${I18N}.saveFailed`));
      } finally {
        setSaving(false);
      }
    },
    [id, step, editable, patchWorkflow, t],
  );

  /** Rewrite the fields of the selected document, leaving every other one alone. */
  const updateSelectedDocument = useCallback(
    (mutate: (fields: SignatureField[]) => SignatureField[]) => {
      if (!config || !selectedPdfId) return;
      const existing = config.documents.find((d) => d.pdfEntryId === selectedPdfId);
      const nextFields = mutate(existing?.fields ?? []);
      const documents: SignatureDocument[] = existing
        ? config.documents.map((d) =>
            d.pdfEntryId === selectedPdfId ? { ...d, fields: nextFields } : d,
          )
        : [...config.documents, { pdfEntryId: selectedPdfId, fields: nextFields }];
      // A document emptied of every field is dropped rather than saved empty —
      // the wire contract gives documents[].fields minItems: 1.
      void persist({
        ...config,
        documents: documents.filter((d) => d.fields.length > 0),
      });
    },
    [config, selectedPdfId, persist],
  );

  const handlePlace = useCallback(
    (pageIndex: number, point: { x: number; y: number }, viewport: PageViewport) => {
      if (!activeSlug) return;
      updateSelectedDocument((current) => {
        // If this signatory already has a field placed in the OTHER language but
        // still missing this one, complete that field instead of minting a new
        // one. Without this, authoring EN then FR always produces two
        // single-language fields rather than one field with both placements —
        // each half looks fine on screen (its own language's view renders it)
        // but the backend's per-field language-parity check rejects both, since
        // it checks each field's own en/frPlacement pair, not the role's totals.
        // FIFO over array order: the first still-incomplete field for this role
        // is the one being completed, matching the order they were placed in.
        //
        // EVERY KIND is eligible — see `signatureFieldCompletion.ts` for why,
        // and for the regression it guards.
        const completingIdx = indexOfFieldToComplete(current, activeSlug, lang);
        if (completingIdx !== -1) {
          // Sized from the field being COMPLETED, not from the signature
          // default — the box has to match the half already authored in the
          // other language.
          const completed = placementAtCssPoint(
            point,
            boxSizeFor(completingBoxIsInitials(current[completingIdx]!)),
            pageIndex,
            viewport,
          );
          return current.map((f, i) =>
            i === completingIdx
              ? lang === 'fr'
                ? { ...f, frPlacement: completed }
                : { ...f, enPlacement: completed }
              : f,
          );
        }
        // Always dropped as a full signature — initials are the exception, and the
        // toggle on the box is one click away.
        const placement = placementAtCssPoint(point, boxSizeFor(false), pageIndex, viewport);
        const n = nextFieldNumber(current);
        return [
          ...current,
          // `textAnchor` is a placeholder: `assignAnchors` overwrites it before
          // this ever reaches the API. `name` / `binding` are NOT recomposed
          // server-side — they are validated as-is for per-document uniqueness,
          // so `nextFieldNumber` picking a genuinely free slot is load-bearing.
          {
            name: `sig_field_${n}`,
            binding: `field_${n}`,
            textAnchor: '[pending]',
            role: activeSlug,
            enPlacement: lang === 'en' ? placement : null,
            frPlacement: lang === 'fr' ? placement : null,
            enDatePlacements: [],
            frDatePlacements: [],
            // No date box yet, so nothing to render one with. Set when the date
            // is added, not here — left null until then rather than defaulted
            // ahead of time.
            dateFormat: null,
            dateFontSize: null,
            isInitials: false,
            // No input box either, and the same reasoning for its settings: the
            // subtype is written when the first box is added.
            enInputPlacements: [],
            frInputPlacements: [],
            inputSubtype: null,
            inputValidation: null,
          },
        ];
      });
    },
    [activeSlug, lang, updateSelectedDocument],
  );

  const handleMove = useCallback(
    (key: string, next: SignaturePlacement) => {
      const { anchor, kind, index } = parseBoxKey(key);
      updateSelectedDocument((current) =>
        current.map((f) => {
          if (f.textAnchor !== anchor) return f;
          // Only the dragged companion moves; its siblings keep the positions they
          // were authored at, and the list's ORDER is untouched — reordering would
          // renumber every `label<i>` / `Input<n>` after it.
          if (kind === 'date' && index !== null) {
            return withDatePlacements(
              f,
              lang,
              datePlacementsOf(f, lang).map((d, i) => (i === index ? next : d)),
            );
          }
          if (kind === 'input' && index !== null) {
            return withInputPlacements(
              f,
              lang,
              inputPlacementsOf(f, lang).map((p, i) => (i === index ? next : p)),
            );
          }
          return lang === 'fr' ? { ...f, frPlacement: next } : { ...f, enPlacement: next };
        }),
      );
    },
    [lang, updateSelectedDocument],
  );

  const handleDelete = useCallback(
    (key: string) => {
      const { anchor, kind, index } = parseBoxKey(key);
      updateSelectedDocument((current) =>
        current.flatMap((f) => {
          if (f.textAnchor !== anchor) return [f];
          // The companions after this one shift down an index, which is exactly
          // what should happen: the payload and the stamped white text are both
          // generated from this list in one run, so they stay in step.
          if (kind === 'date' && index !== null) {
            return [
              withDatePlacements(
                f,
                lang,
                datePlacementsOf(f, lang).filter((_, i) => i !== index),
              ),
            ];
          }
          if (kind === 'input' && index !== null) {
            return [
              withoutOrphanInputSettings(
                withInputPlacements(
                  f,
                  lang,
                  inputPlacementsOf(f, lang).filter((_, i) => i !== index),
                ),
              ),
            ];
          }
          // Deleting the signature in THIS language takes its companions with it —
          // a date label and an input box both anchor off their signature, so one
          // without the other names a position nothing references. The other
          // language keeps its own box; the field only disappears once neither
          // language places it.
          const cleared = withoutOrphanInputSettings(
            withInputPlacements(
              withDatePlacements(
                lang === 'fr' ? { ...f, frPlacement: null } : { ...f, enPlacement: null },
                lang,
                [],
              ),
              lang,
              [],
            ),
          );
          return cleared.enPlacement || cleared.frPlacement ? [cleared] : [];
        }),
      );
    },
    [lang, updateSelectedDocument],
  );

  const handleAddDate = useCallback(
    (anchor: string) => {
      if (!measureDate) return;
      updateSelectedDocument((current) =>
        current.map((f) => {
          if (f.textAnchor !== anchor) return f;
          const signature = placementOf(f, lang);
          if (!signature) return f;
          // The first date box on this signature also settles how it renders.
          // Written explicitly rather than left null: from here on the box is
          // sized to these tokens — always the fixed default, since there is no
          // per-signature setting anymore.
          const { format, fontSize } = dateSettingsOf(f);
          const date = defaultDatePlacement(signature, measureDate(format, fontSize));
          const next = { ...f, dateFormat: format, dateFontSize: fontSize };
          return withDatePlacements(next, lang, [...datePlacementsOf(f, lang), date]);
        }),
      );
    },
    [lang, measureDate, updateSelectedDocument],
  );

  /**
   * Add another date box to a signature that already has one, copied from the
   * date the originator clicked.
   *
   * Appended rather than inserted beside its source: the list's order is what
   * assigns each date its `label<i>` anchor, and the PDF's white text is
   * generated from the same list in the same run — but a box the originator
   * expects to be "the second one" reading a number one higher than they expect
   * is still confusing enough to be worth not doing.
   *
   * ONE LANGUAGE ONLY, the language on screen. The EN and FR PDFs are separate
   * uploads with different layouts, so a copy dropped blind into the French page
   * would land wherever the English one happened to sit — on top of whatever is
   * actually there. The originator switches language and duplicates again, the
   * same way every other box on this page is authored.
   */
  const handleDuplicateDate = useCallback(
    (anchor: string, index: number) => {
      updateSelectedDocument((current) =>
        current.map((f) => {
          if (f.textAnchor !== anchor) return f;
          const dates = datePlacementsOf(f, lang);
          const source = dates[index];
          if (!source || dates.length >= MAX_DATE_PLACEMENTS_PER_SIGNATURE) return f;
          return withDatePlacements(f, lang, [...dates, duplicateDatePlacement(source)]);
        }),
      );
    },
    [lang, updateSelectedDocument],
  );

  /**
   * Flip one placement between a full signature and initials.
   *
   * Written to the FIELD, not to a language's placement: the kind says *what* is
   * collected rather than where it sits, so — like `conditions` and `dateFormat`
   * — it is shared by both PDFs and a box switched while viewing English is
   * initials in French too. The stored width of each language's box follows it
   * so the two never disagree; height and origin are left alone, which keeps the
   * box pinned to the bottom-left corner the anchor is stamped at.
   */
  const handleToggleInitials = useCallback(
    (anchor: string) => {
      updateSelectedDocument((current) =>
        current.map((f) => {
          if (f.textAnchor !== anchor) return f;
          const isInitials = !(f.isInitials ?? false);
          const { width } = boxSizeFor(isInitials);
          return {
            ...f,
            isInitials,
            enPlacement: f.enPlacement ? { ...f.enPlacement, width } : f.enPlacement,
            frPlacement: f.frPlacement ? { ...f.frPlacement, width } : f.frPlacement,
          };
        }),
      );
    },
    [updateSelectedDocument],
  );

  /**
   * Add a text box the signer types into, as a companion of one signature.
   *
   * The same shape as `handleAddDate`: one language only — the EN and FR PDFs are
   * separate uploads, so a box dropped blind into the other language's page would
   * land wherever this one happens to sit — and appended, since the list's order is
   * what assigns each box its `Input<n>` anchor.
   *
   * The first box also settles how every input on this signature behaves:
   * `inputSubtype` is written here, the way `dateFormat` is written with the first
   * date. `inputValidation` stays null until the originator sets a rule of their
   * own; BMO still receives a complete `validation` block, because the BFF fills
   * the unset members from the platform defaults
   * (`ESignFieldValidation.ForInput`).
   */
  const handleAddInput = useCallback(
    (anchor: string) => {
      updateSelectedDocument((current) =>
        current.map((f) => {
          if (f.textAnchor !== anchor) return f;
          const signature = placementOf(f, lang);
          if (!signature) return f;
          const inputs = inputPlacementsOf(f, lang);
          if (inputs.length >= MAX_INPUT_PLACEMENTS_PER_SIGNATURE) return f;
          return withInputPlacements({ ...f, inputSubtype: f.inputSubtype ?? 'TEXTFIELD' }, lang, [
            ...inputs,
            defaultInputPlacement(signature, inputs.length),
          ]);
        }),
      );
    },
    [lang, updateSelectedDocument],
  );

  /**
   * Add another input box to a signature that already has one, copied from the box
   * the originator clicked — the exact counterpart of `handleDuplicateDate`,
   * appended and one language only for the same reasons.
   */
  const handleDuplicateInput = useCallback(
    (anchor: string, index: number) => {
      updateSelectedDocument((current) =>
        current.map((f) => {
          if (f.textAnchor !== anchor) return f;
          const inputs = inputPlacementsOf(f, lang);
          const source = inputs[index];
          if (!source || inputs.length >= MAX_INPUT_PLACEMENTS_PER_SIGNATURE) return f;
          return withInputPlacements(f, lang, [...inputs, duplicateInputPlacement(source)]);
        }),
      );
    },
    [lang, updateSelectedDocument],
  );

  /**
   * Save the show-conditions for one signature.
   *
   * Written into the field and persisted through the page's existing full-config
   * PUT — there is no per-signature endpoint, and there does not need to be.
   * Conditions are shared by both languages (they say WHETHER the signature is
   * wanted, not where it sits), so this is written once regardless of which
   * language is on screen.
   */
  const handleSaveConditions = useCallback(
    (anchor: string, conditions: FileEntryCondition[], combinator: 'all' | 'any' | null) => {
      updateSelectedDocument((current) =>
        current.map((f) =>
          f.textAnchor === anchor ? { ...f, conditions, conditionCombinator: combinator } : f,
        ),
      );
      setConditionAnchor(null);
    },
    [updateSelectedDocument],
  );

  /**
   * Whether the gear is offered at all. A workflow with no eligible source field
   * anywhere before the e-signature step would open the modal on an empty
   * picker, so the control is withheld — the same gate every other conditions
   * trigger uses.
   */
  const canEditConditions = useMemo(
    () =>
      step !== null &&
      hasEligibleSourceFields(workflow?.steps ?? [], { kind: 'step', ownStepId: step.id }),
    [workflow, step],
  );

  /** The field whose conditions modal is open, resolved from the anchor. */
  const conditionField = useMemo(
    () => fields.find((f) => f.textAnchor === conditionAnchor) ?? null,
    [fields, conditionAnchor],
  );

  if (loading || !workflow) {
    return <div style={{ padding: 24, color: '#6b7280', fontSize: 13 }}>{t('common.loading')}</div>;
  }

  if (!step || !config) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <PageHeader title={t(`${I18N}.title`)} onBack={goToDetails} />
        <Empty description={t(`${I18N}.noESignStep`)} style={{ marginTop: 60 }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PageHeader
        title={t(`${I18N}.title`)}
        titleSuffix={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <Tooltip title={t(`${I18N}.helpAria`)}>
              <button
                type="button"
                aria-label={t(`${I18N}.helpAria`)}
                onClick={() => setHelpOpen(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                <QuestionCircleOutlined />
              </button>
            </Tooltip>
            <StatusTag
              status={workflow.status}
              isActive={workflow.isActive}
              hasPendingApproval={!!workflow.pendingApprovedVersionNumber}
              scope="template"
            />
          </span>
        }
        onBack={goToDetails}
      />

      <BmoModal
        open={helpOpen}
        onCancel={() => setHelpOpen(false)}
        title={t(`${I18N}.helpTitle`)}
        footer={
          <Button type="primary" onClick={() => setHelpOpen(false)}>
            {t('common.gotIt')}
          </Button>
        }
      >
        <p style={{ margin: 0, lineHeight: 1.6 }}>{t(`${I18N}.helpIntro`)}</p>
        {/* Directly under the intro, and red rather than the amber the two
            informational panels below use: this is the one thing on this screen
            that goes wrong silently — a clipped signature surfaces at signing
            time, on a package nobody can re-place. */}
        <div
          style={{
            marginTop: 16,
            padding: '12px 14px',
            borderRadius: 10,
            background: '#fef2f2',
            border: '1px solid #fecaca',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#991b1b' }}>
            {t(`${I18N}.helpMarginsTitle`)}
          </div>
          <p style={{ margin: 0, color: '#991b1b', lineHeight: 1.6 }}>{t(`${I18N}.helpMargins`)}</p>
        </div>
        <div
          style={{
            marginTop: 12,
            padding: '12px 14px',
            borderRadius: 10,
            background: '#eff8ff',
            border: '1px solid #bae0fd',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#0b4a6f' }}>
            {t(`${I18N}.helpLanguageTitle`)}
          </div>
          <p style={{ margin: 0, color: '#0b4a6f', lineHeight: 1.6 }}>
            {t(`${I18N}.helpLanguage`)}
          </p>
        </div>
        <div
          style={{
            marginTop: 12,
            padding: '12px 14px',
            borderRadius: 10,
            background: '#fff7ed',
            border: '1px solid #fde68a',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#92400e' }}>
            {t(`${I18N}.helpAnchorTitle`)}
          </div>
          <p style={{ margin: 0, color: '#92400e', lineHeight: 1.6 }}>{t(`${I18N}.helpAnchor`)}</p>
        </div>
      </BmoModal>

      {!editable && (
        <div
          role="status"
          style={{
            padding: '10px 14px',
            borderRadius: 10,
            background: '#fff7ed',
            border: '1px solid #fde68a',
            color: '#92400e',
            fontSize: 13,
          }}
        >
          {t(`${I18N}.readOnly`)}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0, height: '90vh' }}>
        {/* Left: signatories. No field mapping here — that stays in the drawer. */}
        <div
          style={{
            width: 360,
            flexShrink: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            overflow: 'hidden',
            height: '80vh',
          }}
        >
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
              {t(`${I18N}.signatoriesTitle`)}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {t(`${I18N}.signatoriesHint`)}
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {roles.length === 0 ? (
              <div style={{ padding: 24 }}>
                <Empty description={t(`${I18N}.noSignatories`)} />
              </div>
            ) : (
              roles.map((role) => (
                <SignatoryRow
                  key={role.slug}
                  role={role}
                  label={labelBySlug.get(role.slug) ?? role.slug}
                  color={colorBySlug.get(role.slug) ?? SIGNER_COLORS[0]!}
                  active={role.slug === activeSlug}
                  counts={countsFor(config, role.slug, pdfEntries)}
                  onSelect={() => setActiveSlug(role.slug)}
                />
              ))
            )}
          </div>
        </div>

        {/* Right: the PDF */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            overflow: 'hidden',
            height: '80vh',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 14px',
              borderBottom: '1px solid #f0f0f0',
              flexWrap: 'wrap',
            }}
          >
            <Select
              style={{ minWidth: 220 }}
              placeholder={t('pdfMapping.selectPdf')}
              value={selectedPdfId ?? undefined}
              onChange={setSelectedPdfId}
              options={pdfEntries.map((e) => ({ value: e.id, label: e.label }))}
            />
            <Segmented
              value={lang}
              onChange={(v) => setLang(v as 'en' | 'fr')}
              options={[
                { label: t('pdfMapping.english'), value: 'en' },
                { label: t('pdfMapping.french'), value: 'fr' },
              ]}
            />
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              <Button
                size="small"
                type="text"
                icon={<ZoomOutOutlined style={{ fontSize: 13 }} />}
                disabled={zoomIndex === 0}
                aria-label={t('pdfMapping.zoomOut')}
                onClick={() => setZoomIndex((z) => Math.max(0, z - 1))}
              />
              <button
                type="button"
                aria-label={t('pdfMapping.zoomLevel', { percent: Math.round(zoom * 100) })}
                title={t('pdfMapping.zoomReset')}
                onClick={() => setZoomIndex(DEFAULT_ZOOM_INDEX)}
                style={{
                  border: 0,
                  background: 'none',
                  padding: '0 2px',
                  minWidth: 40,
                  fontSize: 12,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  color: '#475569',
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
              >
                {Math.round(zoom * 100)}%
              </button>
              <Button
                size="small"
                type="text"
                icon={<ZoomInOutlined style={{ fontSize: 13 }} />}
                disabled={zoomIndex === ZOOM_STEPS.length - 1}
                aria-label={t('pdfMapping.zoomIn')}
                onClick={() => setZoomIndex((z) => Math.min(ZOOM_STEPS.length - 1, z + 1))}
              />
            </div>
            <LanguageParity fields={fields} entry={selectedEntry} />
            <div
              style={{
                marginLeft: 'auto',
                fontSize: 12,
                color: activeSlug ? '#0179C1' : '#6b7280',
              }}
            >
              {activeSlug
                ? t(`${I18N}.activeHint`, { signer: labelBySlug.get(activeSlug) ?? activeSlug })
                : t(`${I18N}.selectSignatoryFirst`)}
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'auto', background: '#f3f4f6', padding: 20 }}>
            {pdfEntries.length === 0 ? (
              <Empty description={t('pdfMapping.noPdfs')} style={{ marginTop: 60 }} />
            ) : !fileKey ? (
              <Empty description={t('pdfMapping.noFileForLang')} style={{ marginTop: 60 }} />
            ) : (
              <PdfSignaturePlacementViewer
                // Remount on document / language change so pdf.js state resets cleanly.
                key={`${selectedPdfId}:${lang}`}
                templateId={workflow.id}
                fileKey={fileKey}
                boxes={boxes}
                zoom={zoom}
                disabled={!editable || !activeSlug || saving}
                onPlace={handlePlace}
                onMove={handleMove}
                onDelete={handleDelete}
                onAddDate={handleAddDate}
                onDuplicateDate={handleDuplicateDate}
                onToggleInitials={handleToggleInitials}
                onAddInput={handleAddInput}
                onDuplicateInput={handleDuplicateInput}
                onEditConditions={canEditConditions ? setConditionAnchor : null}
              />
            )}
          </div>
        </div>
      </div>

      {conditionField && step && (
        <ConditionsEditorModal
          open
          title={t(`${I18N}.conditionsTitle`)}
          intro={t(`${I18N}.conditionsIntro`)}
          initialConditions={conditionField.conditions ?? []}
          initialCombinator={conditionField.conditionCombinator ?? null}
          allSteps={workflow.steps}
          // Sources are restricted to steps BEFORE the e-signature step — the
          // same scope the drawer uses for signer gating, for the same reason: a
          // signature cannot depend on an answer given after it is signed.
          scope={{ kind: 'step', ownStepId: step.id }}
          saving={saving}
          onCancel={() => setConditionAnchor(null)}
          onSave={(conditions, combinator) =>
            handleSaveConditions(conditionField.textAnchor, conditions, combinator)
          }
        />
      )}
    </div>
  );
}

/**
 * "2 in English · 1 in French" plus a warning when they disagree.
 *
 * The count is per document, because that is what is on screen. The asymmetry is
 * called out rather than left to the submit gate: an originator who places
 * everything in English and forgets French should learn it here, not when the
 * approver bounces the workflow back.
 */
function LanguageParity({
  fields,
  entry,
}: {
  fields: SignatureField[];
  entry: PdfSourceFiles | null;
}) {
  const { t } = useTranslation();
  const en = fields.filter((f) => f.enPlacement).length;
  const fr = fields.filter((f) => f.frPlacement).length;
  if (en === 0 && fr === 0) return null;
  const mismatched = fields.filter((f) => isMissingAPlacement(f, entry)).length;
  return (
    <Tooltip title={mismatched > 0 ? t(`${I18N}.parityWarning`, { count: mismatched }) : undefined}>
      <Tag color={mismatched > 0 ? 'warning' : 'default'} style={{ marginInlineEnd: 0 }}>
        {t(`${I18N}.parityCounts`, { en, fr })}
      </Tag>
    </Tooltip>
  );
}

function SignatoryRow({
  role,
  label,
  color,
  active,
  counts,
  onSelect,
}: {
  role: SignatoryRole;
  label: string;
  color: string;
  active: boolean;
  counts: { en: number; fr: number; incomplete: number };
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const gap = counts.incomplete > 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '10px 14px',
        border: 'none',
        borderBottom: '1px solid #f5f5f5',
        borderLeft: active ? `3px solid ${color}` : '3px solid transparent',
        background: active ? '#eff8ff' : '#fff',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span
          aria-hidden
          style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            background: color,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 13, color: '#111827' }}>{label}</span>
        {role.type === 'advisor' && (
          <Tag style={{ marginInlineEnd: 0 }}>{t(`${I18N}.advisor`)}</Tag>
        )}
      </div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {counts.en === 0 && counts.fr === 0 ? (
          <Tag style={{ marginInlineEnd: 0 }}>{t(`${I18N}.notPlaced`)}</Tag>
        ) : (
          <Tag color={gap ? 'warning' : 'green'} style={{ marginInlineEnd: 0 }}>
            {t(`${I18N}.parityCounts`, counts)}
          </Tag>
        )}
      </div>
    </button>
  );
}

/** How many boxes this signatory has across EVERY document, per language, and how many of its
 *  signatures still lack a box in a language their document needs one in. */
function countsFor(
  config: OneSpanConfig,
  slug: string,
  entries: ReadonlyArray<PdfSourceFiles & { id: string }>,
): { en: number; fr: number; incomplete: number } {
  let en = 0;
  let fr = 0;
  let incomplete = 0;
  for (const doc of config.documents) {
    const entry = entries.find((e) => e.id === doc.pdfEntryId) ?? null;
    for (const field of doc.fields) {
      if (field.role !== slug) continue;
      if (field.enPlacement) en++;
      if (field.frPlacement) fr++;
      if (isMissingAPlacement(field, entry)) incomplete++;
    }
  }
  return { en, fr, incomplete };
}

type PdfSourceFiles = { enFileKey?: string | null; frFileKey?: string | null };

/**
 * Whether this signature still needs a box somewhere — the rule Send for Approval enforces
 * (`WorkflowTemplate.EnsureSignaturePlacementLanguageParity`), mirrored so the warning here and
 * the refusal there never disagree.
 *
 * A box is required in every language the document HAS a PDF for. A document in one language
 * only is signed on that PDF by applications in both languages, so its other language never
 * needs a box. With no file known at all, both are required — nothing proves one is absent.
 */
function isMissingAPlacement(field: SignatureField, entry: PdfSourceFiles | null): boolean {
  if (!field.enPlacement && !field.frPlacement) return false;
  const hasEn = Boolean(entry?.enFileKey);
  const hasFr = Boolean(entry?.frFileKey);
  const needsEn = hasEn || (!hasEn && !hasFr);
  const needsFr = hasFr || (!hasEn && !hasFr);
  return (needsEn && !field.enPlacement) || (needsFr && !field.frPlacement);
}

/**
 * How this signature's date renders, with the defaults filled in.
 *
 * There is no UI to change this per signature — `formatString`/`fontSize` are no
 * longer sent to BMO at all (v8 dropped them), so every date box uses the same
 * fixed default rather than something an originator picks.
 *
 * The format therefore ignores what is stored: a box authored under an earlier
 * default still renders — and re-measures — as the current one, instead of a
 * workflow keeping slashes forever because nobody can reach the setting that
 * wrote them. The stored value converges on the next date add or drag. The font
 * size still falls back, since it has never changed out from under a stored box.
 */
function dateSettingsOf(field: SignatureField): { format: string; fontSize: number } {
  return {
    format: DEFAULT_DATE_FORMAT,
    fontSize: field.dateFontSize ?? DEFAULT_DATE_FONT_SIZE,
  };
}

/** This language's signature box, or null when it is not placed there. */
function placementOf(field: SignatureField, lang: 'en' | 'fr'): SignaturePlacement | null {
  return (lang === 'fr' ? field.frPlacement : field.enPlacement) ?? null;
}

/**
 * This language's date boxes, in the order that assigns their `label<i>`
 * anchors. Empty rather than null for a signature with no date, so callers can
 * spread it without a guard.
 */
function datePlacementsOf(field: SignatureField, lang: 'en' | 'fr'): SignaturePlacement[] {
  return (lang === 'fr' ? field.frDatePlacements : field.enDatePlacements) ?? [];
}

/** Rewrite one language's date list, leaving the other language untouched. */
function withDatePlacements<T extends SignatureField>(
  field: T,
  lang: 'en' | 'fr',
  dates: SignaturePlacement[],
): T {
  return lang === 'fr'
    ? { ...field, frDatePlacements: dates }
    : { ...field, enDatePlacements: dates };
}

/**
 * This language's input boxes, in the order that assigns their `Input<n>`
 * anchors. Empty rather than null for a signature with none, so callers can
 * spread it without a guard.
 */
function inputPlacementsOf(field: SignatureField, lang: 'en' | 'fr'): SignaturePlacement[] {
  return (lang === 'fr' ? field.frInputPlacements : field.enInputPlacements) ?? [];
}

/** Rewrite one language's input list, leaving the other language untouched. */
function withInputPlacements<T extends SignatureField>(
  field: T,
  lang: 'en' | 'fr',
  inputs: SignaturePlacement[],
): T {
  return lang === 'fr'
    ? { ...field, frInputPlacements: inputs }
    : { ...field, enInputPlacements: inputs };
}

/**
 * Drop the input subtype and validation once the signature has no input box left
 * in either language.
 *
 * Not cosmetic: they describe boxes that no longer exist, and
 * `WorkflowStep.SetOneSpanConfig` refuses that pairing — so a signature whose last
 * input was deleted would fail to save with an error naming a rule the originator
 * never broke. Unlike the date settings, which the domain tolerates as harmless
 * leftovers.
 */
function withoutOrphanInputSettings<T extends SignatureField>(field: T): T {
  if ((field.enInputPlacements?.length ?? 0) > 0 || (field.frInputPlacements?.length ?? 0) > 0) {
    return field;
  }
  return { ...field, inputSubtype: null, inputValidation: null };
}

/**
 * The e-signature step to author against.
 *
 * The drawer passes its step id in the query string, so a workflow that somehow
 * ends up with more than one lands on the right one. Falling back to the first
 * configured step keeps a bookmarked URL working.
 */
function findESignStep(workflow: WorkflowTemplate, stepId: string | null): WorkflowStep | null {
  const eSignSteps = workflow.steps.filter((s) => s.type === 'OneSpanESign');
  return eSignSteps.find((s) => s.id === stepId) ?? eSignSteps[0] ?? null;
}
